/**
 * Frontend Subscription Configuration
 * Defines the display information for subscription tiers
 */

export const parentSubscriptions = {
  // Free tier
  free: {
    name: "Free",
    title: "Try it free",
    description: "Basic access to Meira platform",
    price: {
      annual: "Free",
      quarterly: "Free",
    },
    features: [
      "Access to 3 activities",
      "Connect with 1 mentor",
      "Limited content access",
      "Basic support",
    ],
    buttonText: "Current Plan",
    highlighted: false,
    disabled: true, // Cannot select this plan
  },

  // Essential tier
  essential: {
    name: "Essential",
    title: "Get Started",
    description: "Enhanced access to Meira learning resources",
    price: {
      annual: "KES 5,999/yr",
      quarterly: "KES 1,799/qtr",
    },
    perMonth: {
      annual: "About KES 500/mo",
      quarterly: "About KES 600/mo",
    },
    features: [
      "Register 1 child",
      "Unlimited activities",
      "Connect with 5 mentors",
      "Premium content access",
      "Video consultations",
      "Smart learning recommendations",
      "Progress reports",
      "Custom learning paths",
      "Access to 10 learning products",
      "Priority support",
      "Dedicated learning advisor",
      "AI-powered learning insights",
    ],
    buttonText: "Choose Essential",
    highlighted: false,
    color: "brand.100",
  },

  // Growth tier
  growth: {
    name: "Growth",
    title: "Most Popular",
    description: "Comprehensive access to Meira learning platform",
    price: {
      annual: "KES 9,599/yr",
      quarterly: "KES 2,879/qtr",
    },
    perMonth: {
      annual: "About KES 800/mo",
      quarterly: "About KES 960/mo",
    },
    features: [
      "Register 2 children",
      "Unlimited activities",
      "Connect with 5 mentors",
      "Premium content access",
      "Video consultations",
      "Smart learning recommendations",
      "Progress reports",
      "Custom learning paths",
      "Access to 10 learning products",
      "Priority support",
      "Dedicated learning advisor",
      "AI-powered learning insights",
    ],
    buttonText: "Choose Growth",
    highlighted: true,
    color: "brand.500",
  },

  // Plus tier
  plus: {
    name: "Plus",
    title: "Full Experience",
    description: "Premium access to all Meira features",
    price: {
      annual: "KES 14,999/yr",
      quarterly: "KES 4,499/qtr",
    },
    perMonth: {
      annual: "About KES 1,250/mo",
      quarterly: "About KES 1,500/mo",
    },
    features: [
      "Register unlimited children",
      "Unlimited activities",
      "Connect with 5 mentors",
      "Premium content access",
      "Video consultations",
      "Smart learning recommendations",
      "Progress reports",
      "Custom learning paths",
      "Access to 10 learning products",
      "Priority support",
      "Dedicated learning advisor",
      "AI-powered learning insights",
    ],
    buttonText: "Choose Plus",
    highlighted: false,
    color: "purple.500",
  },
};

export const schoolSubscriptions = {
  // Ignite tier
  ignite: {
    name: "Ignite",
    title: "School Essentials",
    description: "Essential Meira platform for schools",
    price: {
      annual: "Free",
    },
    features: [
      "Unlimited students",
      "Unlimited teachers",
      "Unlimited classrooms",
      "Standard content access",
      "Progress tracking",
      "Parent portal",
      "Admin dashboard",
      "Bulk user import",
    ],
    buttonText: "Choose Ignite",
    highlighted: false,
    color: "orange.500",
  },

  // Elevate tier
  elevate: {
    name: "Elevate",
    title: "School Premium",
    description: "Complete Meira platform for growing schools",
    price: {
      annual: "KES 10/yr",
    },
    features: [
      "Unlimited students",
      "Unlimited teachers",
      "Unlimited classrooms",
      "Premium content access",
      "Progress tracking",
      "Parent portal",
      "Admin dashboard",
      "Bulk user import",
      "Custom branding",
      "API access",
      "Dedicated support",
      "Training webinars",
      "Analytics reports",
      "Custom integrations",
    ],
    buttonText: "Choose Elevate",
    highlighted: true,
    color: "teal.500",
  },
};

// FAQ content for subscription page
export const subscriptionFAQ = [
  {
    question: "How do I change my subscription plan?",
    answer: "You can change your subscription plan at any time by going to your account settings and selecting 'Subscription'. From there, you can upgrade or downgrade your plan as needed."
  },
  {
    question: "Can I cancel my subscription?",
    answer: "Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period. To cancel, go to your account settings and select 'Subscription', then click on 'Cancel Subscription'."
  },
  {
    question: "Is there a free trial?",
    answer: "We don't currently offer a free tier for parents, but schools can use the Ignite tier for free. Please check our subscription plans for more details."
  },
  {
    question: "How do payments work?",
    answer: "We offer both M-Pesa and card payment options. Payments are processed securely, and you'll receive a receipt via email after each successful payment."
  },
  {
    question: "What's the difference between quarterly and annual billing?",
    answer: "Annual billing offers greater savings compared to quarterly billing. With annual billing, you pay once per year at a discounted rate."
  },
  {
    question: "I'm a school administrator. Which plan should I choose?",
    answer: "Schools can use the Ignite tier for free, which now offers unlimited students, teachers, and classrooms. The Elevate plan offers additional premium features like custom branding, API access, and dedicated support for growing schools."
  },
  {
    question: "Can I switch between parent and school subscriptions?",
    answer: "Parent and school subscriptions are separate. If you need to switch, please contact our support team who will assist you with the transition."
  },
  {
    question: "Will my subscription automatically renew?",
    answer: "You can choose whether to enable auto-renewal for your subscription. If enabled, your subscription will automatically renew at the end of your billing period. You can change this setting in your account."
  }
];

export default {
  parentSubscriptions,
  schoolSubscriptions,
  subscriptionFAQ
}; 